import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { apiDelete, apiGet, apiPost, apiPut } from "../../shared/apiClient";
import { useToast } from "../../shared/ToastContext";
import {
  ProductEditorFormFields,
  defaultProductForm
} from "./ProductEditorFormFields";

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function getRowValue(row, key) {
  if (!row || !key) return undefined;
  if (Object.prototype.hasOwnProperty.call(row, key)) return row[key];
  const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
  if (Object.prototype.hasOwnProperty.call(row, pascalKey)) return row[pascalKey];
  return row[key] ?? row[pascalKey];
}

function mapImages(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((item, index) => ({
      id: item.id ?? item.Id ?? undefined,
      image: item.image ?? item.Image ?? "",
      order: Number(item.order ?? item.Order ?? index),
      _key: `img-${item.id ?? item.Id ?? index}`
    }))
    .filter((item) => item.image)
    .sort((a, b) => a.order - b.order);
}

export function EditProductPage() {
  const { id: idParam } = useParams();
  const id = idParam && /^\d+$/.test(String(idParam)) ? Number(idParam) : NaN;
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const [customers, setCustomers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [htmlMode, setHtmlMode] = useState(false);
  const [form, setForm] = useState({ ...defaultProductForm });
  const [originalImageIds, setOriginalImageIds] = useState([]);

  const loadLookups = useCallback(async (customerId) => {
    if (!customerId) {
      setCategories([]);
      setBrands([]);
      return;
    }
    const q = `customerId=${encodeURIComponent(customerId)}`;
    const [catRes, brandRes] = await Promise.all([
      apiGet(`/api/ProductCategories?${q}`),
      apiGet(`/api/Brands?${q}`)
    ]);
    setCategories(catRes.ok && Array.isArray(catRes.data) ? catRes.data : []);
    setBrands(brandRes.ok && Array.isArray(brandRes.data) ? brandRes.data : []);
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    void (async () => {
      const res = await apiGet("/api/Customers");
      if (res.ok) setCustomers(Array.isArray(res.data) ? res.data : []);
    })();
  }, [isAdmin]);

  useEffect(() => {
    if (!Number.isFinite(id)) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const res = await apiGet(`/api/Products/${id}`);
      if (cancelled) return;
      setLoading(false);
      if (!res.ok) {
        toast.push(String(res.error || "Failed to load product"), "error");
        return;
      }
      const row = res.data;
      const images = mapImages(row.images ?? row.Images);
      setOriginalImageIds(images.filter((x) => x.id).map((x) => x.id));
      const customerId = Number(getRowValue(row, "customerId") ?? user?.customerId ?? 0);
      setForm({
        id: row.id,
        customerId: String(customerId || ""),
        code: getRowValue(row, "code") ?? "",
        barcode: getRowValue(row, "barcode") ?? "",
        name: getRowValue(row, "name") ?? "",
        subtitle: getRowValue(row, "subtitle") ?? "",
        shortDescription: getRowValue(row, "shortDescription") ?? "",
        description: getRowValue(row, "description") ?? "",
        content: getRowValue(row, "content") ?? "",
        detailContentMode: getRowValue(row, "detailContentMode") === "json" ? "json" : "html",
        sectionsJson: getRowValue(row, "sectionsJson") ?? "",
        categoryId: String(getRowValue(row, "categoryId") ?? ""),
        brandId: getRowValue(row, "brandId") ? String(getRowValue(row, "brandId")) : "",
        unitName: getRowValue(row, "unitName") ?? "",
        price: Number(getRowValue(row, "price") ?? 0),
        discountPrice: (() => {
          const v = getRowValue(row, "discountPrice");
          return v == null || v === "" ? "" : String(v);
        })(),
        minQuantity: Number(getRowValue(row, "minQuantity") ?? 1),
        image: getRowValue(row, "image") ?? "",
        order: Number(getRowValue(row, "order") ?? 0),
        images
      });
      if (customerId) await loadLookups(customerId);
    })();
    return () => {
      cancelled = true;
    };
  }, [id, user?.customerId, toast, loadLookups]);

  useEffect(() => {
    if (form.customerId) void loadLookups(form.customerId);
  }, [form.customerId, loadLookups]);

  const syncImages = async (productId, nextImages) => {
    const current = Array.isArray(nextImages) ? nextImages : [];
    const currentIds = new Set(current.filter((x) => x.id).map((x) => x.id));

    for (const imageId of originalImageIds) {
      if (!currentIds.has(imageId)) {
        const res = await apiDelete(`/api/Products/images/${imageId}`);
        if (!res.ok) throw new Error(res.error || "Failed to delete product image");
      }
    }

    for (const [index, item] of current.entries()) {
      if (item.id) {
        const res = await apiPut(`/api/Products/images/${item.id}`, {
          id: item.id,
          productId,
          image: item.image,
          order: index
        });
        if (!res.ok) throw new Error(res.error || "Failed to update product image");
      } else if (item.image) {
        const res = await apiPost(`/api/Products/${productId}/images`, {
          image: item.image,
          order: index
        });
        if (!res.ok) throw new Error(res.error || "Failed to add product image");
      }
    }
  };

  const onClone = async () => {
    if (!Number.isFinite(id)) return;
    const confirmed = window.confirmDialog
      ? await window.confirmDialog("Clone this product? A new copy will be created.")
      : window.confirm("Clone this product? A new copy will be created.");
    if (!confirmed) return;

    setCloning(true);
    const res = await apiPost(`/api/Products/${id}/clone`, {});
    setCloning(false);
    if (!res.ok) {
      toast.push(String(res.error || "Failed to clone product"), "error");
      return;
    }
    const newId = Number(res.data?.id ?? res.data?.Id ?? 0);
    toast.push("Product cloned", "success");
    if (newId) navigate(`/products/${newId}/edit`);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!Number.isFinite(id)) return;

    const customerId = isAdmin
      ? Number(form.customerId || 0)
      : Number(user?.customerId || 0);
    if (!customerId) {
      toast.push("Customer is required", "error");
      return;
    }
    if (!String(form.name || "").trim()) {
      toast.push("Name is required", "error");
      return;
    }
    if (!Number(form.categoryId)) {
      toast.push("Category is required", "error");
      return;
    }

    const discountRaw = String(form.discountPrice ?? "").trim();
    const payload = {
      id,
      customerId,
      code: String(form.code || "").trim() || null,
      barcode: String(form.barcode || "").trim() || null,
      name: String(form.name || "").trim(),
      subtitle: String(form.subtitle || "").trim() || null,
      shortDescription: String(form.shortDescription || "").trim() || null,
      description: String(form.description || "").trim() || null,
      content: form.content || null,
      detailContentMode: form.detailContentMode === "json" ? "json" : "html",
      sectionsJson: String(form.sectionsJson || "").trim() || null,
      categoryId: Number(form.categoryId),
      brandId: form.brandId ? Number(form.brandId) : null,
      unitName: String(form.unitName || "").trim() || null,
      price: toNumber(form.price, 0),
      discountPrice: discountRaw === "" ? null : toNumber(discountRaw, 0),
      minQuantity: Math.max(1, toNumber(form.minQuantity, 1)),
      image: String(form.image || "").trim() || null,
      order: toNumber(form.order, 0)
    };

    setSaving(true);
    try {
      const res = await apiPut(`/api/Products/${id}`, payload);
      if (!res.ok) {
        toast.push(String(res.error || "Failed to update product"), "error");
        return;
      }
      await syncImages(id, form.images);
      toast.push("Product updated", "success");
      navigate("/products");
    } catch (err) {
      toast.push(String(err?.message || err || "Failed to save product images"), "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">Loading...</div>;
  }

  return (
    <div className="w-full space-y-4">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Edit product</h1>
            <p className="mt-1 text-sm text-slate-500">Update catalog details and landing page content.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void onClone()}
              disabled={cloning}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              {cloning ? "Cloning..." : "Clone"}
            </button>
            <Link
              to={`/products/${id}/preview`}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Preview content
            </Link>
            <button
              type="button"
              onClick={() => navigate("/products")}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Back
            </button>
            <button
              type="submit"
              form="edit-product-form"
              disabled={saving}
              className="rounded-lg bg-brand-800 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <form id="edit-product-form" onSubmit={onSubmit}>
          <ProductEditorFormFields
            form={form}
            setForm={setForm}
            isAdmin={isAdmin}
            customers={customers}
            categories={categories}
            brands={brands}
            htmlMode={htmlMode}
            setHtmlMode={setHtmlMode}
          />
        </form>
      </div>
    </div>
  );
}
